import printJobManager, { PrintJob } from './printJobManager';
import { 
  renderCartItemsReceipt, 
  renderOrderReceipt, 
  renderKitchenReceipt, 
  renderSessionReceipt,
  renderKitchenReceiptCutByDish,
  renderKitchenReceiptCutByCategory,
  renderKitchenReceiptWithPrice,
  renderPOSSalesReport
} from './receiptRenderer';
import canvasToEscpos from './canvasToEscpos';
import { loadPrinterPrintStyle, PrintStyle } from './printStyle';
import { activePrintCommand, isPrintCommandPending, styleForPrintCommand } from './printCommand';
import { canvasToStarLine } from './starLine';
import { canvasToStarGraphic } from './starGraphic';
import { canvasToStarPrnt } from './starPrnt';
import { bagLabelText, cupLabelText, deliverySlipText, kitchenSlipText, orderSlipText, salesReportText, sessionSlipText, waiterSlipText } from './slipText';
import { renderWaiterReceipts } from './tableKitchenCanvas';
import { renderBagLabel, renderCupLabels, renderDeliverySlip } from './labelCanvas';

function labelJobFields(job: PrintJob) {
  return {
    cupLabel: Boolean(job.cupLabel),
    cupLabelPaper: job.cupLabelPaper === 'linerless' ? 'linerless' : 'receipt',
    bagLabel: Boolean(job.bagLabel),
    deliverySlip: Boolean(job.deliverySlip),
    isDeliveryOrder: Boolean(job.isDeliveryOrder),
    guestName: job.guestName || '',
    orderNumber: job.orderNumber || '',
    deliveryAddress: job.deliveryAddress || null,
  };
}

function appendLabelCanvases(canvases: HTMLCanvasElement[], order: any, store: any, printer: any) {
  return canvases
    .concat(renderCupLabels(order, store, printer))
    .concat(renderBagLabel(order, store, printer))
    .concat(renderDeliverySlip(order, store, printer));
}

function appendLabelText(buffers: Uint8Array[], order: any, store: any, printer: any, style: PrintStyle) {
  return buffers
    .concat(cupLabelText(order, store, printer, style))
    .concat(bagLabelText(order, store, printer, style))
    .concat(deliverySlipText(order, store, printer, style));
}

/**
 * Job Processor Service
 * Processes print jobs from the queue continuously
 */
class JobProcessor {
  private isRunning: boolean = false;
  private intervalId: NodeJS.Timeout | null = null;
  private processingJobId: number | null = null;

  /**
   * Start processing jobs
   */
  start(): void {
    if (this.isRunning) {
      console.log('[PRINTER DEBUG] [JobProcessor] Already running');
      return;
    }

    this.isRunning = true;
    console.log('[PRINTER DEBUG] [JobProcessor] Starting job processor');

    // Process jobs every 500ms
    this.intervalId = setInterval(() => {
      this.processNextJob().catch(error => {
        console.error('[PRINTER DEBUG] [JobProcessor] Error processing job:', error);
      });
    }, 500);
  }

  /**
   * Stop processing jobs
   */
  stop(): void {
    if (!this.isRunning) {
      return;
    }

    this.isRunning = false;
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    console.log('[PRINTER DEBUG] [JobProcessor] Stopped job processor');
  }

  /**
   * Process the next pending job
   */
  private async processNextJob(): Promise<void> {
    // Don't process if already processing a job
    if (this.processingJobId !== null) {
      console.log(`[PRINTER DEBUG] [JobProcessor] Skipping - already processing job ${this.processingJobId}`);
      return;
    }

    try {
      const pendingJobs = await printJobManager.getPendingJobs();
      if (pendingJobs.length === 0) {
        return;
      }

      console.log(`[REPEAT DEBUG] [JobProcessor] Found ${pendingJobs.length} pending job(s)`);

      // Get the first pending job
      const job = pendingJobs[0];
      console.log(`[REPEAT DEBUG] [JobProcessor] Attempting to process job ${job.id}, type: ${job.jobType}, status: ${job.status}`);
      
      // CRITICAL FIX: Try to mark as processing - only succeeds if job is still pending
      // This prevents multiple cycles from processing the same job
      const claimed = await printJobManager.markJobProcessing(job.id!);
      if (!claimed) {
        console.log(`[REPEAT DEBUG] [JobProcessor] Job ${job.id} was already claimed by another cycle, skipping`);
        return;
      }
      
      console.log(`[REPEAT DEBUG] [JobProcessor] Successfully claimed job ${job.id} for processing`);
      
      // Now set in-memory lock to prevent this instance from processing another job
      this.processingJobId = job.id!;

      console.log(`[PRINTER DEBUG] [JobProcessor] ===== STARTING JOB ${job.id} =====`);
      console.log(`[PRINTER DEBUG] [JobProcessor] Job type: ${job.jobType}`);
      console.log(`[PRINTER DEBUG] [JobProcessor] Printer query:`, job.printerQuery);
      console.log(`[PRINTER DEBUG] [JobProcessor] Job config:`, {
        isKitchenSlip: job.isKitchenSlip,
        kitchenSlipWithPrice: job.kitchenSlipWithPrice,
        cutbyItem: job.cutbyItem,
        cutbyCategory: job.cutbyCategory
      });
      if (job.cartItemsData) {
        console.log(`[PRINTER DEBUG] [JobProcessor] Cart items (${job.cartItemsData.length}):`, 
          job.cartItemsData.map((item: any) => `${item.name || item.id} (x${item.amount})`));
      }

      await this.executeJob(job);

      console.log(`[REPEAT DEBUG] [JobProcessor] Job ${job.id} execution finished, marking as completed`);
      const completed = await printJobManager.markJobCompleted(job.id!);
      
      if (completed) {
        console.log(`[REPEAT DEBUG] [JobProcessor] ===== COMPLETED JOB ${job.id} =====`);
        
        // Verify job is actually completed
        const verifyJob = await printJobManager.getPrintJob(job.id!);
        if (verifyJob?.status !== 'completed') {
          console.error(`[REPEAT DEBUG] [JobProcessor] ERROR: Job ${job.id} status is ${verifyJob?.status} after completion!`);
        }
      } else {
        console.error(`[REPEAT DEBUG] [JobProcessor] ERROR: Failed to mark job ${job.id} as completed - job was not in processing status!`);
      }
    } catch (error) {
      if (this.processingJobId) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        await printJobManager.markJobFailed(this.processingJobId, errorMessage, true);
        console.error(`[PRINTER DEBUG] [JobProcessor] ===== FAILED JOB ${this.processingJobId} =====`, errorMessage);
      }
    } finally {
      this.processingJobId = null;
    }
  }

  /**
   * Execute a print job
   */
  private async executeJob(job: PrintJob): Promise<void> {
    // Find printer
    const printer = await this.findPrinter(job.printerQuery);
    if (!printer) {
      throw new Error(`Printer not found for query: ${JSON.stringify(job.printerQuery)}`);
    }

    const style = await loadPrinterPrintStyle(printer.api_key);
    const command = activePrintCommand(printer);
    const commandStyle = styleForPrintCommand(style, command);
    if (commandStyle.mode === 'Text') {
      const buffers = this.buildTextBuffers(job, printer, commandStyle);
      console.log('[Print style] USING TEXT MODE', {
        jobType: job.jobType,
        apiKey: printer.api_key,
        brand: command,
      });
      for (const buffer of buffers) {
        await this.sendToPrinter(printer, buffer);
      }
      return;
    }
    if (command === 'STARIMPACT') {
      const buffers = this.buildTextBuffers(job, printer, commandStyle);
      console.log('[Print style] USING IMPACT TEXT', {
        jobType: job.jobType,
        apiKey: printer.api_key,
      });
      for (const buffer of buffers) {
        await this.sendToPrinter(printer, buffer);
      }
      return;
    }
    console.log('[Print style] USING IMAGE MODE', {
      apiKey: printer.api_key,
      brand: command,
    });

    // Render receipt to canvas(es)
    let canvases: HTMLCanvasElement[] = [];
    
    switch (job.jobType) {
      case 'cart_items':
        // Support both cartItems and cartItemsData field names
        const cartItems = job.cartItems || (job as any).cartItemsData;
        if (!cartItems || cartItems.length === 0) {
          throw new Error('No cart items provided');
        }
        
        // Convert cart items to order-like format for kitchen receipt rendering
        const orderLikeData = {
          items: cartItems,
          tableName: job.kitchenTitle || job.storeData.tableName || 'Counter',
          seatTableName: job.seatTableName || '',
          counter: job.counter ?? '',
          total: 0,
          totalWithTaxes: 0,
          isKitchenSlip: job.isKitchenSlip || false,
          kitchenSlipWithPrice: job.kitchenSlipWithPrice || false,
          cutbyItem: job.cutbyItem || false,
          cutbyCategory: job.cutbyCategory || false,
          isTableOrder: Boolean(job.isTableOrder),
          tableMarkPlacement: job.tableMarkPlacement || '',
          tableNote: job.tableNote || '',
          waiterSlip: Boolean(job.waiterSlip),
          waiterSlipCut: job.waiterSlipCut || 'table',
          ...(job.uberSlip ? { uberSlip: job.uberSlip } : {}),
          ...labelJobFields(job),
        };

        // Use kitchen receipt rendering if isKitchenSlip is true
        if (job.isKitchenSlip) {
          if (job.kitchenSlipWithPrice) {
            // Kitchen slip with price
            const canvas = renderKitchenReceiptWithPrice(orderLikeData, job.storeData, printer);
            canvases = [canvas];
          } else if (job.cutbyItem) {
            // Cut by item (one receipt per item)
            canvases = renderKitchenReceiptCutByDish(orderLikeData, job.storeData, printer);
          } else if (job.cutbyCategory) {
            // Cut by category (one receipt per category)
            canvases = renderKitchenReceiptCutByCategory(orderLikeData, job.storeData, printer);
          } else {
            // Standard kitchen receipt (cut at end)
            const canvas = renderKitchenReceipt(orderLikeData, job.storeData, printer);
            canvases = [canvas];
          }
          if (orderLikeData.waiterSlip && orderLikeData.isTableOrder) {
            canvases = canvases.concat(renderWaiterReceipts(orderLikeData, job.storeData, printer));
          }
          canvases = appendLabelCanvases(canvases, orderLikeData, job.storeData, printer);
        } else {
          // Use temporary bill receipt for non-kitchen items
          const canvas = renderCartItemsReceipt(cartItems, job.storeData);
          canvases = appendLabelCanvases([canvas], orderLikeData, job.storeData, printer);
        }
        break;
      
      case 'receipt':
        if (!job.orderData) {
          throw new Error('No order data provided');
        }
        const receiptCanvas = renderOrderReceipt(job.orderData, job.storeData);
        canvases = [receiptCanvas];
        break;
      
      case 'kitchen':
        if (!job.orderData) {
          throw new Error('No order data provided');
        }
        const kitchenCanvas = renderKitchenReceipt(job.orderData, job.storeData, printer);
        canvases = [kitchenCanvas];
        break;
      
      case 'session':
        if (!job.sessionData) {
          throw new Error('No session data provided');
        }
        // Get tables from store data if available
        const tables = job.storeData.tables || [];
        const sessionCanvas = renderSessionReceipt(
          job.sessionData,
          job.storeData,
          'session_qr',
          tables
        );
        canvases = [sessionCanvas];
        break;
      
      case 'sales_report':
        if (!job.reportData) {
          throw new Error('No report data provided');
        }
        if (!job.stationStartTime || !job.endTime) {
          throw new Error('Station start time and end time required for sales report');
        }
        const salesReportCanvas = renderPOSSalesReport(
          job.reportData,
          job.storeData,
          job.stationStartTime,
          job.endTime
        );
        canvases = [salesReportCanvas];
        break;
      
      default:
        throw new Error(`Unknown job type: ${job.jobType}`);
    }

    if (canvases.length === 0) {
      throw new Error('No canvases generated');
    }

    console.log(`[PRINTER DEBUG] [JobProcessor] Generated ${canvases.length} canvas(es) for job ${job.id}`);
    console.log(`[PRINTER DEBUG] [JobProcessor] Printer: ${printer.name} (${printer.api_key || 'N/A'})`);
    console.log(`[PRINTER DEBUG] [JobProcessor] Job config:`, {
      isKitchenSlip: job.isKitchenSlip,
      kitchenSlipWithPrice: job.kitchenSlipWithPrice,
      cutbyItem: job.cutbyItem,
      cutbyCategory: job.cutbyCategory,
      itemCount: job.cartItems?.length || (job as any).cartItemsData?.length || 0
    });

    // Process each canvas (for cut-by-item/category, there will be multiple canvases)
    for (let i = 0; i < canvases.length; i++) {
      const canvas = canvases[i];
      console.log(`[PRINTER DEBUG] [JobProcessor] Processing canvas ${i + 1}/${canvases.length} for job ${job.id}`);
      // Convert canvas to ESC/POS
      const buffer = command === 'STARGRAPHIC'
        ? canvasToStarGraphic(canvas)
        : command === 'STARPRNT'
        ? canvasToStarPrnt(canvas)
        : command === 'STARLINE'
        ? canvasToStarLine(canvas, (canvas as HTMLCanvasElement & { skipLabelFeed?: boolean }).skipLabelFeed ? 0 : 3)
        : canvasToEscpos(canvas, {
          beepEnabled: printer.beep_enabled || false,
          numberOfBeeps: printer.number_of_beeps || 2,
          lineFeeds: (canvas as HTMLCanvasElement & { skipLabelFeed?: boolean }).skipLabelFeed ? 0 : 3,
        });

      // Send to printer
      console.log(`[PRINTER DEBUG] [JobProcessor] Sending canvas ${i + 1}/${canvases.length} to printer ${printer.name} (${printer.api_key || 'N/A'})`);
      await this.sendToPrinter(printer, buffer);
      console.log(`[PRINTER DEBUG] [JobProcessor] Successfully sent canvas ${i + 1}/${canvases.length} to printer`);
      
      // Add delay between prints for proper cutting (reused from silent-printer)
      if (canvases.length > 1) {
        console.log(`[PRINTER DEBUG] [JobProcessor] Waiting 500ms before next canvas...`);
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    }
    
    console.log(`[PRINTER DEBUG] [JobProcessor] Finished processing all ${canvases.length} canvas(es) for job ${job.id}`);
  }

  private buildTextBuffers(job: PrintJob, printer: any, style: PrintStyle): Uint8Array[] {
    if (job.jobType === 'cart_items') {
      const cartItems = job.cartItems || (job as any).cartItemsData;
      const orderLikeData = {
        items: cartItems,
        tableName: job.kitchenTitle || job.storeData.tableName || 'Counter',
        seatTableName: job.seatTableName || '',
        counter: job.counter ?? '',
        total: 0,
        cutbyItem: job.cutbyItem || false,
        cutbyCategory: job.cutbyCategory || false,
        isTableOrder: Boolean(job.isTableOrder),
        tableMarkPlacement: job.tableMarkPlacement || '',
        tableNote: job.tableNote || '',
        waiterSlip: Boolean(job.waiterSlip),
        waiterSlipCut: job.waiterSlipCut || 'table',
        ...(job.uberSlip ? { uberSlip: job.uberSlip } : {}),
        ...labelJobFields(job),
      };
      if (job.isKitchenSlip) {
        const kitchen = kitchenSlipText(orderLikeData, job.storeData, printer, style, Boolean(job.kitchenSlipWithPrice));
        const withWaiter = orderLikeData.waiterSlip && orderLikeData.isTableOrder
          ? kitchen.concat(waiterSlipText(orderLikeData, job.storeData, printer, style))
          : kitchen;
        return appendLabelText(withWaiter, orderLikeData, job.storeData, printer, style);
      }
      return appendLabelText([orderSlipText(orderLikeData, job.storeData, printer, style)], orderLikeData, job.storeData, printer, style);
    }
    if (job.jobType === 'kitchen') {
      return kitchenSlipText(job.orderData, job.storeData, printer, style, false);
    }
    if (job.jobType === 'receipt') {
      return [orderSlipText(job.orderData, job.storeData, printer, style)];
    }
    if (job.jobType === 'session') {
      return [sessionSlipText(job.sessionData, job.storeData, printer, style)];
    }
    if (job.jobType === 'sales_report' && job.stationStartTime && job.endTime) {
      return [salesReportText(job.reportData, job.storeData, printer, style, job.stationStartTime, job.endTime)];
    }
    throw new Error(`Text mode is not available for job type: ${job.jobType}`);
  }

  /**
   * Find printer by query
  private async nextRunnableJob(jobs: PrintJob[]): Promise<PrintJob | null> {
    for (const job of jobs) {
      const printer = await this.findPrinter(job.printerQuery);
      if (printer && isPrintCommandPending(printer)) {
        console.log('[Print command] live job held', printer.name || printer.api_key);
        continue;
      }
      return job;
    }
    return null;
  }

   */
  private async findPrinter(query: { api_key?: string; print_qr_receipt?: boolean }): Promise<any> {
    if (!window.api || !window.api.findPrinters) {
      throw new Error('Electron API not available');
    }

    console.log(`[PRINTER DEBUG] [JobProcessor] Finding printer with query:`, query);
    const printers = await window.api.findPrinters();
    console.log(`[PRINTER DEBUG] [JobProcessor] Found ${printers.length} total printer(s) in database`);
    printers.forEach((p: any, index: number) => {
      console.log(`[PRINTER DEBUG] [JobProcessor]   Printer ${index + 1}: ${p.name} - API Key: ${p.api_key || 'N/A'}`);
    });
    
    if (query.api_key) {
      console.log(`[PRINTER DEBUG] [JobProcessor] Looking for printer with API key: ${query.api_key}`);
      const printer = printers.find((p: any) => p.api_key === query.api_key);
      if (printer) {
        console.log(`[PRINTER DEBUG] [JobProcessor] Found printer: ${printer.name} (${printer.api_key})`);
        return printer;
      } else {
        console.warn(`[PRINTER DEBUG] [JobProcessor] No printer found with API key: ${query.api_key}`);
        return null;
      }
    }

    if (query.print_qr_receipt) {
      console.log(`[PRINTER DEBUG] [JobProcessor] Looking for printer with print_qr_receipt: true`);
      const printer = printers.find((p: any) => p.print_qr_receipt === true);
      if (printer) {
        console.log(`[PRINTER DEBUG] [JobProcessor] Found QR receipt printer: ${printer.name}`);
        return printer;
      } else {
        console.warn(`[PRINTER DEBUG] [JobProcessor] No printer found with print_qr_receipt: true`);
        return null;
      }
    }

    console.warn(`[PRINTER DEBUG] [JobProcessor] No valid query provided`);
    return null;
  }

  /**
   * Send buffer to printer
   */
  private async sendToPrinter(printer: any, buffer: Uint8Array): Promise<void> {
    if (!window.api) {
      throw new Error('Electron API not available');
    }

    const callback = (error: any) => {
      if (error) {
        throw new Error(error);
      }
    };

    if (printer.printer_type === 'Network ESC/POS') {
      if (!window.api.printNetwork) {
        throw new Error('printNetwork API not available');
      }
      await window.api.printNetwork(
        printer.network_port || 9100,
        printer.network_address,
        buffer,
        callback
      );
    } else if (printer.printer_type === 'Windows Shared ESC/POS') {
      if (!window.api.printSerial) {
        throw new Error('printSerial API not available');
      }
      await window.api.printSerial(printer.windows_shared_name, buffer, callback);
    } else {
      throw new Error(`Invalid printer type: ${printer.printer_type}`);
    }

    // Add delay between prints
    await new Promise((resolve) => setTimeout(resolve, 500));
  }

  /**
   * Get processing status
   */
  isProcessing(): boolean {
    return this.processingJobId !== null;
  }
}

// Singleton instance
const jobProcessor = new JobProcessor();

export default jobProcessor;

export {};

